import { Temporal } from '@insio/dates';
import { CreditStatus } from '@insio/shared-types';
import { ProfileMenuUsageUnit, type ProfileMenuUsage } from '@insio/ui-toolkit';
import type { CreditUsageFieldsFragment } from '@/graphql';

/** Who reads the credit usage: its payer (figures), a client of a credit-paying master (status only), or nobody. */
export const CreditViewer = {
  NONE: 'NONE',
  PAYER: 'PAYER',
  MANAGED_CLIENT: 'MANAGED_CLIENT'
} as const;

export type CreditViewer = (typeof CreditViewer)[keyof typeof CreditViewer];

type CreditUsageView = CreditUsageFieldsFragment | null | undefined;

/** `null` is a documents payer; `undefined` is no answer yet. */
export function creditViewerOf(usage: CreditUsageView): CreditViewer {
  if (usage?.figures) return CreditViewer.PAYER;
  if (usage?.paidBy) return CreditViewer.MANAGED_CLIENT;
  return CreditViewer.NONE;
}

/** Whether the account's AI work is paid in credits, by itself or by its master account. */
export function isCreditBilled(usage: CreditUsageView): boolean {
  return usage != null;
}

/** "November 1, 2026" — the period end as a calendar date in UTC, as the document quota shows it. */
export function formatCreditPeriodEnd(periodEnd: Temporal.Instant, locale: string): string {
  return periodEnd
    .toZonedDateTimeISO('UTC')
    .toPlainDate()
    .toLocaleString(locale, { day: 'numeric', month: 'long', year: 'numeric' });
}

interface CreditMenuUsageOptions {
  locale: string;
  planName: string | null;
  planHref?: string;
}

/** The profile menu's credits panel; `null` for anyone but the payer, who alone sees figures. */
export function toCreditMenuUsage(
  usage: CreditUsageFieldsFragment,
  { locale, planName, planHref }: CreditMenuUsageOptions
): ProfileMenuUsage | null {
  const figures = usage.figures;
  if (!figures) return null;

  return {
    unit: ProfileMenuUsageUnit.CREDITS,
    used: figures.usedInPeriod ?? 0,
    limit: figures.allotment,
    extraRemaining: figures.packRemaining + figures.manualRemaining,
    available: figures.balance,
    renews: figures.renews,
    exhausted: usage.status === CreditStatus.EXHAUSTED,
    resetDate: figures.periodEnd ? formatCreditPeriodEnd(figures.periodEnd, locale) : null,
    planName,
    planHref
  };
}

export type CreditsBannerView =
  | { viewer: typeof CreditViewer.PAYER; canManageBilling: boolean }
  | { viewer: typeof CreditViewer.MANAGED_CLIENT; masterName: string };

/** What the exhausted-credits banner says, or `null` while credits allow AI work. */
export function creditsBannerView(
  usage: CreditUsageView,
  canManageBilling: boolean
): CreditsBannerView | null {
  if (usage?.status !== CreditStatus.EXHAUSTED) return null;

  const viewer = creditViewerOf(usage);
  if (viewer === CreditViewer.PAYER) return { viewer, canManageBilling };
  if (viewer === CreditViewer.MANAGED_CLIENT && usage.paidBy) {
    return { viewer, masterName: usage.paidBy.name };
  }
  return null;
}
